import React, { useCallback } from 'react';

import { MenuSectionGridItem } from 'features/menu/components/menu-section/menu-section-grid-item';
import { MenuSectionListItem } from 'features/menu/components/menu-section/menu-section-list-item';
import { IMenuSectionProps } from 'features/menu/components/menu-section/types';
import { Separator } from 'features/menu/components/menu-tile-grid/separator';
import { MENU_TILE_GRID_HALF_GAP } from 'features/menu/components/menu-tile-grid/styled';
import { FlatList, ListRenderItemInfo, StyleSheet } from 'react-native';

import { centeredRowContainerStyles } from 'components/layout/centered-row-container';
import LoadingAnimation from 'components/loading-animation';
import { AvailableMenuCategoriesHeader } from 'experiments/deeplinks-flow-feature/treatment/features/menu/components/menu-section/available-menu-categories-header';
import { OtherAvailableOptionsHeader } from 'experiments/deeplinks-flow-feature/treatment/features/menu/components/menu-section/other-available-options-header';
import { OtherAvailableSectionsHeader } from 'experiments/deeplinks-flow-feature/treatment/features/menu/components/menu-section/other-available-sections-header';
import {
  getFirstAvailableMenuItemIndex,
  shouldShowOtherAvailableOptionsHeader,
} from 'experiments/deeplinks-flow-feature/treatment/features/menu/utils/sort-menu-section-items';
import { useIsMobileBreakpoint } from 'hooks/use-media-query';
import { IMainMenuNode } from 'state/unified-menu/types';
import { spacing } from 'styles/constants';

// NEW one menu (web)
export const MenuSection = React.memo<IMenuSectionProps>(
  ({
    data,
    pullToReloadProps,
    footerSpacer,
    deeplinkUnavailableItemId,
    onUnavailableChooseRestaurant,
    otherAvailableHeader = 'options',
    hidePrices = false,
  }) => {
    const isMobile = useIsMobileBreakpoint();
    // Unavailable deeplink recovery uses list chrome (badge/footer/CTA). Keep that
    // layout on desktop web too — the grid tile path has no recovery UI.
    const useUnavailableListLayout = Boolean(deeplinkUnavailableItemId);
    const useListLayout = isMobile || useUnavailableListLayout;
    const showOtherAvailableHeader = shouldShowOtherAvailableOptionsHeader(
      data,
      deeplinkUnavailableItemId
    );
    const firstAvailableIndex = getFirstAvailableMenuItemIndex(data, deeplinkUnavailableItemId);

    const renderItem = useCallback(
      ({ item, index }: ListRenderItemInfo<IMainMenuNode>) => {
        if (!useListLayout) {
          return <MenuSectionGridItem item={item} hidePrices={hidePrices} />;
        }

        const isUnavailable = Boolean(
          deeplinkUnavailableItemId && item.id === deeplinkUnavailableItemId
        );

        return (
          <>
            {showOtherAvailableHeader && index === firstAvailableIndex ? (
              otherAvailableHeader === 'categories' ? (
                <AvailableMenuCategoriesHeader />
              ) : otherAvailableHeader === 'sections' ? (
                <OtherAvailableSectionsHeader />
              ) : (
                <OtherAvailableOptionsHeader />
              )
            ) : null}
            <MenuSectionListItem
              item={item}
              deeplinkUnavailable={isUnavailable}
              hidePrices={hidePrices}
              onUnavailableChooseRestaurant={
                isUnavailable ? onUnavailableChooseRestaurant : undefined
              }
            />
          </>
        );
      },
      [
        deeplinkUnavailableItemId,
        firstAvailableIndex,
        hidePrices,
        onUnavailableChooseRestaurant,
        otherAvailableHeader,
        showOtherAvailableHeader,
        useListLayout,
      ]
    );

    const renderSeparator = useCallback(
      ({ leadingItem }: { leadingItem: IMainMenuNode }) => {
        // Skip the hairline between the unavailable category and the
        // "Available Menu Categories" header / first available sibling.
        if (deeplinkUnavailableItemId && leadingItem?.id === deeplinkUnavailableItemId) {
          return null;
        }
        return <Separator />;
      },
      [deeplinkUnavailableItemId]
    );

    return (
      <FlatList
        key={useListLayout ? 'list' : 'desktop-grid'}
        data={data}
        {...pullToReloadProps}
        style={{ flex: 1, marginHorizontal: useListLayout ? 0 : MENU_TILE_GRID_HALF_GAP * -1 }}
        contentContainerStyle={[
          centeredRowContainerStyles.content,
          { alignSelf: 'center' },
          useUnavailableListLayout ? styles.unavailableFeedContent : null,
        ]}
        ItemSeparatorComponent={useListLayout ? renderSeparator : null}
        ListFooterComponent={
          useListLayout ? (
            <>
              <Separator />
              {footerSpacer}
            </>
          ) : null
        }
        numColumns={useListLayout ? 1 : 3}
        ListEmptyComponent={<LoadingAnimation paddingY={spacing.$4} />}
        showsVerticalScrollIndicator={false}
        keyExtractor={section => section.id}
        renderItem={renderItem}
      />
    );
  }
);

const styles = StyleSheet.create({
  unavailableFeedContent: {
    paddingTop: spacing.$2,
  },
});
